import React, { useEffect, useMemo, useRef, useState } from 'react';
import DragSortableList from 'react-drag-sortable';
import { ReactComponent as DragIcon } from '../../../assets/svg/drag_indicator_24px.svg';
import C, { UserCan } from '../../../shared/constants';
import {
  getHideExtraInfoValue,
  getPinVisibility, getReportFilterVisibility, getTagEditFieldVisibility,
  getTravelReportFieldVisibility,
  setHideExtraInfo,
  setPinVisibilityValue,
  setReportFilterVisibility, setTagEditFieldVisibility,
  setTravelReportFieldVisibility
} from "../../../shared/db/extra-info-db";
import {
  DeviceInfoSectionOrderItem,
  getDeviceInfoSectionFullOrder, getDeviceInfoSectionHiddenMap, setDeviceInfoHidden,
  setDeviceInfoSectionOrder,
} from '../../../shared/db/general-db';
import {
  changePermission,
  changeShowIconOnMap,
  getShowIconOnMap,
  usersHavePermission
} from "../../../shared/db/people-db";
import { getHideLargeReportWarning, getMinimalisticReportView, setDBMinimalisticReportView, setHideLargeReportWarning } from '../../../shared/db/report-db';
import { clientDb } from '../../../shared/firebase';
import { ExtraInfoFields, iVisibilitySettings, MenuPinType, ReportFilterSectionName, TagEditFieldName } from "../../../shared/interfaces";
import { localStorage } from "../../../shared/storage";
import { useRedux } from "../../../states/redux-state";
import { getExtraInfoKeyByName } from "../../../utils";
import LocalPersonTile from '../../pages/PersonTabPage/LocalPeopleTile';
import styles from './AllUserFeatures.module.scss';

const deviceSectionNameByID: {[id in DeviceInfoSectionOrderItem]: string} = {
  [DeviceInfoSectionOrderItem.LAST_PING]: 'Last Ping Details',
  [DeviceInfoSectionOrderItem.TAGS]: 'Tags',
  [DeviceInfoSectionOrderItem.EXTRA_INFO]: 'Extra info',
  [DeviceInfoSectionOrderItem.VISIBLE_TO]: 'Visible to',
  [DeviceInfoSectionOrderItem.ASSIGNED_DRIVER]: 'Assigned driver',
  [DeviceInfoSectionOrderItem.ALERT]: 'Alert',
  [DeviceInfoSectionOrderItem.ENGINE_HISTORY]: 'Engine history',
  [DeviceInfoSectionOrderItem.NOTES]: 'Notes',
}

// #region styles
const main: React.CSSProperties = {
  width: '100%',
  margin: '3px 3px 6px',
};
const head: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  backgroundColor: '#fff',
  borderRadius: '4px'
};
const body: React.CSSProperties = {
  width: '100%',
  padding: '5px 10px',
  border: `1px solid ${C.lightGray}`,
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 4,
};
const toggle: React.CSSProperties = {
  color: '#111',
  opacity: 0.5,
  cursor: 'pointer',
  width: '20px',
  height: '20px'
};
const title: React.CSSProperties = {
  flex: '1 1 0%',
  color: '#fff',
  fontWeight: 'bold',
};
const table: React.CSSProperties = {
  // width: '200px'
};
const key: React.CSSProperties = {
  fontWeight: 'bold',
  marginBottom: 15
};
const checkboxLabel: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  margin: '5px 0'
};
const sectionTitle: React.CSSProperties = {...key, marginTop: 15};
// #endregion

const deviceInfo: ({
  name: string;
  permission: UserCan;
})[] = [
  {
    permission: UserCan.HIDE_DEVICE_STATUS,
    name: 'Status',
  },
  {
    permission: UserCan.HIDE_DEVICE_VIEW,
    name: 'View',
  },
  {
    permission: UserCan.HIDE_DEVICE_HISTORY,
    name: 'History',
  },
  {
    permission: UserCan.HIDE_DEVICE_LOCATE,
    name: 'Locate',
  },
  {
    permission: UserCan.HIDE_DEVICE_TODAYS_HISTORY,
    name: 'Today’s History',
  },
  {
    permission: UserCan.HIDE_DEVICE_FUEL,
    name: 'Fuel',
  },
  {
    permission: UserCan.HIDE_DEVICE_BATTERY,
    name: 'Battery',
  },
  {
    permission: UserCan.HIDE_DEVICE_VOLT,
    name: 'Volt',
  },
  {
    permission: UserCan.HIDE_DEVICE_BATTERY_PERCENT,
    name: 'Battery percent',
  },
  {
    permission: UserCan.HIDE_DEVICE_CHECK_ENGINE,
    name: 'Has check engine',
  },
  {
    permission: UserCan.HIDE_DEVICE_SPEED,
    name: 'Speed',
  },
];

enum TravelReportField {
  Duration = 'duration',
  Idle = 'idle',
  Stopped = 'stopped',
  Safety = 'safety',
  Person = 'person',
  MaxSpeed = 'max_speed',
  AvgSpeed = 'avg_speed',
  Distance = 'distance',
  Consumption = 'consumption',
  Label = 'label'
}

export const AllUsersFeatures = ({isPinned}) => {
  const user = useRedux((s) => s.auth.user);
  const [reports, setReports] = React.useState(false);
  const [tags, setTags] = React.useState(false);
  const [tappedIn, setTappedIn] = React.useState(false);
  const [filterReportsByPeople, setFilterReportsByPeople] = useState<boolean>(false);
  const [ maintenance, setMaintenance ] = React.useState(false);
  const [menuPinSettings, setMenuPinSettings] = useState<{[key: string]: boolean}>({});
  const [collapsed, setCollapsed] = useState<boolean>(isPinned);

  useEffect(() => {
    setCollapsed(isPinned);
  }, [isPinned]);

  // pages setup
  const [hideDashboard, setHideDashboard] = useState(false);

  const pagesSetup = [
    {
      permission: UserCan.HIDE_DASHBOARD_PAGE,
      name: 'Hide dashboard page',
      value: hideDashboard,
      onChange: (newValue: boolean) => {
        setHideDashboard(newValue);
        changePermission(user, UserCan.HIDE_DASHBOARD_PAGE, newValue)();
      }
    },
  ];

  // visibility setup
  const [hideSerial, setHideSerial] = useState<boolean>(false);
  const [pinVisibility, setPinVisibility] = useState<boolean>(true);

  const visibilitySetup: iVisibilitySettings[] = [
    {
      name: 'Device Serial Number',
      label: 'hide_serial',
      value: hideSerial,
      onChange: async (newValue: boolean) => {
        setHideSerial(newValue);

        const key = await getExtraInfoKeyByName(ExtraInfoFields.SERIAL);

        if (key !== '') setHideExtraInfo(user, newValue, key);
      }
    },
    {
      name: 'Devices Menu Pin',
      label: 'pin_visibility',
      value: pinVisibility,
      onChange: async (newValue: boolean) => {
        setPinVisibility(newValue);

        setPinVisibilityValue(user, newValue);
      }
    }
  ]

  // report filter visibility
  const [typeVisibility, setTypeVisibility] = useState<boolean>(true);
  const [devicesVisibility, setDevicesVisibility] = useState<boolean>(true);
  const [peopleVisibility, setPeopleVisibility] = useState<boolean>(true);
  const [tagsVisibility, setTagsVisibility] = useState<boolean>(true);
  const [alertsVisibility, setAlertsVisibility] = useState<boolean>(true);
  const [valuesVisibility, setValuesVisibility] = useState<boolean>(true);
  const [labelsVisibility, setLabelsVisibility] = useState<boolean>(true);
  const [postedSpeedVisibility, setPostedSpeedVisibility] = useState<boolean>(true);
  const [dateVisibility, setDateVisibility] = useState<boolean>(true);

  const [fetchedFilterVisibility, setFetchedFilterVisibility] = useState<boolean>(false);

  // tag edit field visibility
  const [tagColorVisibility, setTagColorVisibility] = useState<boolean>(true);
  const [tagActionsVisibility, setTagActionsVisibility] = useState<boolean>(true);
  const [tagEnableAlertsVisibility, setTagEnableAlertsVisibility] = useState<boolean>(true);

  const [fetchedTagEditVisibility, setFetchedTagEditVisibility] = useState<boolean>(false);

  const tagEditFieldVisibilitySettings: iVisibilitySettings[] = [
    {
      name: 'Tag Color',
      label: 'tag_color_visibility',
      value: tagColorVisibility,
      onChange: async (newValue: boolean) => {
        setTagColorVisibility(newValue);
        setTagEditFieldVisibility(user, TagEditFieldName.COLOR, newValue);
      }
    },
    {
      name: 'Tag Actions',
      label: 'tag_actions_visibility',
      value: tagActionsVisibility,
      onChange: async (newValue: boolean) => {
        setTagActionsVisibility(newValue);
        setTagEditFieldVisibility(user, TagEditFieldName.ACTIONS, newValue);
      }
    },
    {
      name: 'Enable Alerts',
      label: 'tag_enable_alerts_visibility',
      value: tagEnableAlertsVisibility,
      onChange: async (newValue: boolean) => {
        setTagEnableAlertsVisibility(newValue);
        setTagEditFieldVisibility(user, TagEditFieldName.ENABLE_ALERTS, newValue);
      }
    }
  ];

  const reportFilterVisibilitySettings: iVisibilitySettings[] = [
    {
      name: 'Report Type',
      label: 'type_visibility',
      value: typeVisibility,
      onChange: async (newValue: boolean) => {
        setTypeVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.TYPE, newValue)
      }
    },
    {
      name: 'Devices',
      label: 'devices_visibility',
      value: devicesVisibility,
      onChange: async (newValue: boolean) => {
        setDevicesVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.DEVICES, newValue)
      }
    },
    {
      name: 'People',
      label: 'people_visibility',
      value: peopleVisibility,
      onChange: async (newValue: boolean) => {
        setPeopleVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.PEOPLE, newValue)
      }
    },
    {
      name: 'Tags',
      label: 'tags_visibility',
      value: tagsVisibility,
      onChange: async (newValue: boolean) => {
        setTagsVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.TAGS, newValue)
      }
    },
    {
      name: 'Alerts',
      label: 'alerts_visibility',
      value: alertsVisibility,
      onChange: async (newValue: boolean) => {
        setAlertsVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.ALERTS, newValue)
      }
    },
    {
      name: 'Labels',
      label: 'labels_visibility',
      value: labelsVisibility,
      onChange: async (newValue: boolean) => {
        setLabelsVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.LABELS, newValue)
      }
    },
    {
      name: 'Values',
      label: 'values_visibility',
      value: valuesVisibility,
      onChange: async (newValue: boolean) => {
        setValuesVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.VALUES, newValue)
      }
    },
    {
      name: 'Posted Speed',
      label: 'posted_speed_visibility',
      value: postedSpeedVisibility,
      onChange: async (newValue: boolean) => {
        setPostedSpeedVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.POSTED_SPEED, newValue)
      }
    },
    {
      name: 'Date',
      label: 'date_visibility',
      value: dateVisibility,
      onChange: async (newValue: boolean) => {
        setDateVisibility(newValue);

        setReportFilterVisibility(user, ReportFilterSectionName.DATE, newValue)
      }
    }
  ]

  // travel report field visibility setup
  const defaultFieldVisibility: Record<TravelReportField, boolean> = {
    [TravelReportField.Duration]: false,
    [TravelReportField.Idle]: false,
    [TravelReportField.Stopped]: false,
    [TravelReportField.Safety]: false,
    [TravelReportField.Person]: false,
    [TravelReportField.MaxSpeed]: false,
    [TravelReportField.AvgSpeed]: false,
    [TravelReportField.Distance]: false,
    [TravelReportField.Consumption]: false,
    [TravelReportField.Label]: false,
  };

  const [fieldVisibility, setFieldVisibility] = useState<Record<TravelReportField, boolean>>(defaultFieldVisibility);

  const toggleFieldVisibility = async (key: TravelReportField, newValue: boolean) => {
    setFieldVisibility(prev => ({ ...prev, [key]: newValue }));
    setTravelReportFieldVisibility(user, newValue, key);
  };

  const travelReportFieldVisibility = [
    {
      name: 'Duration',
      label: TravelReportField.Duration,
      value: fieldVisibility[TravelReportField.Duration],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Duration, newValue)
    },
    {
      name: 'Idle',
      label: TravelReportField.Idle,
      value: fieldVisibility[TravelReportField.Idle],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Idle, newValue)
    },
    {
      name: 'Stopped',
      label: TravelReportField.Stopped,
      value: fieldVisibility[TravelReportField.Stopped],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Stopped, newValue)
    },
    {
      name: 'Safety',
      label: TravelReportField.Safety,
      value: fieldVisibility[TravelReportField.Safety],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Safety, newValue)
    },
    {
      name: 'Person',
      label: TravelReportField.Person,
      value: fieldVisibility[TravelReportField.Person],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Person, newValue)
    },
    {
      name: 'Max Speed',
      label: TravelReportField.MaxSpeed,
      value: fieldVisibility[TravelReportField.MaxSpeed],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.MaxSpeed, newValue)
    },
    {
      name: 'Avg. Speed',
      label: TravelReportField.AvgSpeed,
      value: fieldVisibility[TravelReportField.AvgSpeed],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.AvgSpeed, newValue)
    },
    {
      name: 'Distance',
      label: TravelReportField.Distance,
      value: fieldVisibility[TravelReportField.Distance],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Distance, newValue)
    },
    {
      name: 'Consumption',
      label: TravelReportField.Consumption,
      value: fieldVisibility[TravelReportField.Consumption],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Consumption, newValue)
    },
    {
      name: 'Label',
      label: TravelReportField.Label,
      value: fieldVisibility[TravelReportField.Label],
      onChange: (newValue: boolean) => toggleFieldVisibility(TravelReportField.Label, newValue)
    },
  ];

  // other setup
  enum ViewMode {
    GRID = 'grid',
    LIST = 'list',
  }

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.GRID);
  const [showLargeReportWarining, setShowLargeReportWarning] = useState<boolean>(true);
  const [minimalisticReportView, setMinimalisticReportView] = useState<boolean>(false);
  const userInfo = localStorage.get('login-init-user');

  const handleGridOrListView = async (newViewMode: ViewMode) => {
    try {
      setViewMode(newViewMode);

      await clientDb().update({
        [`people/people-details/${userInfo.uid}/viewMode`]: newViewMode,
      });
    } catch (error) {
      console.error('Error updating view mode:', error);
    }
  };

  const otherSetup = [
    {
      name: 'Show list view instead of grid view on Devices and People pages',
      label: 'grid_list_toggle',
      value: viewMode === ViewMode.LIST,
      onChange: async (newValue: boolean) => {
        const newViewMode = newValue ? ViewMode.LIST : ViewMode.GRID;
        await handleGridOrListView(newViewMode);
      },
    },
    {
      name: 'Show large report warning',
      label: 'large_report_toggle',
      value: showLargeReportWarining,
      onChange: async (showReportValue: boolean) => {
        await setHideLargeReportWarning(user, !showReportValue);
        setShowLargeReportWarning(showReportValue);
      },
    },
    {
      name: 'Minimalistic Report View',
      label: 'minimalistic_report_view',
      value: minimalisticReportView,
      onChange: async (showReportValue: boolean) => {
        await setDBMinimalisticReportView(user, showReportValue);
        setMinimalisticReportView(showReportValue);
      },
    }
  ];

  // map setup
  const [showMarkerNameAbove, setShowMarkerNameAbove] = useState(false);
  const [showIconOnMap, setShowIconOnMap] = useState<boolean>(false);
  const [roundedMapMarkerImage, setRoundedMapMarkerImage] = useState(false);
  const [simpleCirclePins, setSimpleCirclePins] = useState(false);

  const mapSetup = [
    {
      permission: UserCan.SHOW_MARKER_NAME_ABOVE,
      name: 'Show marker name above',
      value: showMarkerNameAbove,
      onChange: (newValue: boolean) => {
        setShowMarkerNameAbove(newValue);
        changePermission(user, UserCan.SHOW_MARKER_NAME_ABOVE, newValue)();
      }
    },
    {
      label: 'show_device_using_icon',
      name: 'Show device using icon',
      value: showIconOnMap,
      onChange: (newValue: boolean) => {
        setShowIconOnMap(newValue);
        changeShowIconOnMap(user, newValue);
      }
    },
    {
      permission: UserCan.ROUNDED_MAP_MARKER_IMAGE,
      name: 'Rounded map marker image',
      value: roundedMapMarkerImage,
      onChange: (newValue: boolean) => {
        setRoundedMapMarkerImage(newValue);
        changePermission(user, UserCan.ROUNDED_MAP_MARKER_IMAGE, newValue)();
      }
    },
    {
      permission: UserCan.SIMPLE_CIRCLE_PINS,
      name: 'Use simple circle pins for trip points',
      value: simpleCirclePins,
      onChange: (newValue: boolean) => {
        setSimpleCirclePins(newValue);
        changePermission(user, UserCan.SIMPLE_CIRCLE_PINS, newValue)();
      }
    }
  ];

  // device info
  const [deviceInfoConfiguration, setDeviceInfoConfiguration] = useState<Partial<Record<UserCan, boolean>>>({});
  const deviceInfoConfigurationRef = useRef(deviceInfoConfiguration);
  const updateDeviceInfoConfiguration = (newValue: Partial<Record<UserCan, boolean>>) => {
    setDeviceInfoConfiguration(newValue);
    deviceInfoConfigurationRef.current = newValue;
  };
  const updateSingleDeviceInfoConfiguration = (newValue: Partial<Record<UserCan, boolean>>) => {
    updateDeviceInfoConfiguration({...deviceInfoConfigurationRef.current, ...newValue});
  }

  const fetchHideLargeReportWarning = async () => {
    try {
      const value = await getHideLargeReportWarning();
      setShowLargeReportWarning(!value);
    }
    catch (error) {
      console.error('Error fetching large report warning state', error);
    }
  }

  const fetchMinimalisticReportView = async () => {
    try {
      const value = await getMinimalisticReportView();
      setMinimalisticReportView(value);
    }
    catch (error) {
      console.error('Error fetching minimalistic report view', error);
    }
  }

  // fetch device info
  useEffect(() => {
    const clientId = localStorage.get('active-client');

    [
      UserCan.HIDE_DEVICE_STATUS,
      UserCan.HIDE_DEVICE_VIEW,
      UserCan.HIDE_DEVICE_HISTORY,
      UserCan.HIDE_DEVICE_LOCATE,
      UserCan.HIDE_DEVICE_TODAYS_HISTORY,
      UserCan.HIDE_DEVICE_FUEL,
      UserCan.HIDE_DEVICE_BATTERY,
      UserCan.HIDE_DEVICE_VOLT,
      UserCan.HIDE_DEVICE_BATTERY_PERCENT,
      UserCan.HIDE_DEVICE_CHECK_ENGINE,
      UserCan.HIDE_DEVICE_SPEED,
    ].forEach((permission) => {
      usersHavePermission(clientId, permission)
        .then((res) => {
            updateSingleDeviceInfoConfiguration({ [permission]: res });
          }
        )
    });

    const getMenuPins = async () => {
      const fetchedMenuPinSettings = (await clientDb().child(`/people/people-details/${user.uid}/menuPin`).once('value')).val();
      if (fetchedMenuPinSettings) setMenuPinSettings(fetchedMenuPinSettings);
    }

    getMenuPins();
    fetchHideLargeReportWarning();
    fetchMinimalisticReportView();
  }, []);


  const toggleCollapsed = () => setCollapsed(!collapsed);

  const fetchVisibilitySettings = async () => {
    const key = await getExtraInfoKeyByName(ExtraInfoFields.SERIAL);
    const value = await getHideExtraInfoValue(key);
    const pinValue = await getPinVisibility();

    setHideSerial(value);
    setPinVisibility(pinValue);
  }

  const fetchReportFilterVisibility = async () => {
    try {
      const value = await getReportFilterVisibility();

      if (value) {
        setTypeVisibility(value[ReportFilterSectionName.TYPE] !== false);
        setDevicesVisibility(value[ReportFilterSectionName.DEVICES] !== false);
        setPeopleVisibility(value[ReportFilterSectionName.PEOPLE] !== false);
        setTagsVisibility(value[ReportFilterSectionName.TAGS] !== false);
        setAlertsVisibility(value[ReportFilterSectionName.ALERTS] !== false);
        setValuesVisibility(value[ReportFilterSectionName.VALUES] !== false);
        setLabelsVisibility(value[ReportFilterSectionName.LABELS] !== false);
        setPostedSpeedVisibility(value[ReportFilterSectionName.POSTED_SPEED] !== false);
        setDateVisibility(value[ReportFilterSectionName.DATE] !== false);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setFetchedFilterVisibility(true);
    }
  }

  const fetchTagEditFieldVisibility = async () => {
    try {
      const value = await getTagEditFieldVisibility();

      if (value) {
        setTagColorVisibility(value[TagEditFieldName.COLOR] !== false);
        setTagActionsVisibility(value[TagEditFieldName.ACTIONS] !== false);
        setTagEnableAlertsVisibility(value[TagEditFieldName.ENABLE_ALERTS] !== false);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setFetchedTagEditVisibility(true);
    }
  }

  const fetchTravelReportFieldVisibility = async () => {
    const value = await getTravelReportFieldVisibility();

    setFieldVisibility(prev => ({
      ...defaultFieldVisibility,
      ...value
    }));
  }

  const fetchViewMode = async () => {
    const userFirebaseViewMode = (await clientDb().child(`people/people-details/${user.uid}/viewMode`).once('value')).val();
    if (userFirebaseViewMode) {
      setViewMode(userFirebaseViewMode);
    }
  };

  const fetchShowIcon = async () => {
    const value = await getShowIconOnMap(user.uid);

    setShowIconOnMap(value);
  }

  useEffect(() => {
    const clientId = localStorage.get('active-client');
    usersHavePermission(clientId, 'see-tags').then((res) => setTags(res));
    usersHavePermission(clientId, 'see-all-reports').then((res) => setReports(res));
    usersHavePermission(clientId, 'see-tapped-people').then((res) => setTappedIn(res));
    usersHavePermission(clientId, 'see-maintenance').then(res => setMaintenance(res));
    usersHavePermission(clientId, UserCan.FILTER_REPORTS_BY_PEOPLE).then(res => setFilterReportsByPeople(res));

    // pages setup
    usersHavePermission(clientId, UserCan.HIDE_DASHBOARD_PAGE).then(res => setHideDashboard(res));

    // Map setup
    usersHavePermission(clientId, UserCan.SHOW_MARKER_NAME_ABOVE).then(res => setShowMarkerNameAbove(res));
    usersHavePermission(clientId, UserCan.ROUNDED_MAP_MARKER_IMAGE).then(res => setRoundedMapMarkerImage(res));
    usersHavePermission(clientId, UserCan.SIMPLE_CIRCLE_PINS).then(res => setSimpleCirclePins(res));

    fetchVisibilitySettings();
    fetchReportFilterVisibility();
    fetchTagEditFieldVisibility();
    fetchTravelReportFieldVisibility();
    fetchViewMode();
    fetchShowIcon();
  }, []);

  const deviceInfoOptionChange = (permission: UserCan, newOptionValue: boolean) => {
    updateSingleDeviceInfoConfiguration({ [permission]: newOptionValue });
    changePermission(user, permission, newOptionValue)();
  }

  const [deviceInfoSectionOrderState, setDeviceInfoSectionOrderState] = useState<DeviceInfoSectionOrderItem[]>([]);
  useEffect(() => {
    getDeviceInfoSectionFullOrder().then((order) => {
      setDeviceInfoSectionOrderState(order);
    });
  }, []);

  const deviceInfoSectionOrderItems = useMemo(() => (
    deviceInfoSectionOrderState.map(item => ({
      id: item,
      name: deviceSectionNameByID[item],
    }))
  ), [deviceInfoSectionOrderState]);
  const [hiddenMap, setHiddenMap] = useState<{ [key: string]: boolean }>({});
  useEffect(() => {
    const fetchHiddenMap = async () => {
      const map = await getDeviceInfoSectionHiddenMap(deviceInfoSectionOrderItems);
      setHiddenMap(map);
    };
    fetchHiddenMap().then(() => {});
  }, [deviceInfoSectionOrderItems]);

  const toggleHidden = async (name: string) => {
    const newValue = !hiddenMap[name];
    await setDeviceInfoHidden(name, newValue);
    setHiddenMap((prev) => ({ ...prev, [name]: newValue }));
  };

  const onDeviceSectionReorder = (items: {id: DeviceInfoSectionOrderItem }[]) => {
    const newOrder: DeviceInfoSectionOrderItem[] = items.map(({ id }) => id);
    setDeviceInfoSectionOrderState(newOrder);
    setDeviceInfoSectionOrder(user)(newOrder);
  }

  return (
    <div style={{...main}}>
      <LocalPersonTile
        collapsible
        initialCollapsed={menuPinSettings[MenuPinType.ALL_USERS_FEATURES] || false}
        title="All Users Features"
        action={null}
        isPinned={false}
        withPin={false}
        property={MenuPinType.ALL_USERS_FEATURES}
        leftIcon={null}
      >
        <div className={styles.AllUserFeaturesContainer}>
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>User's Features</span>
                <div className={styles.SectionBodyTable}>
                  <div className={styles.CheckboxItem}>
                    <label>Scheduled Reports</label>
                    <input
                      className={styles.Checkbox}
                      type="checkbox"
                      name='Scheduled reports'
                      checked={reports}
                      onChange={() => setReports(!reports)}
                      onClick={changePermission(user, 'see-all-reports', !reports)}
                    />
                  </div>
                  <div className={styles.CheckboxItem}>
                    <label>Maintenance</label>
                    <input
                      className={styles.Checkbox}
                      type="checkbox"
                      name='Maintenance'
                      checked={maintenance}
                      onChange={() => setMaintenance(!maintenance)}
                      onClick={changePermission(user, 'see-maintenance', !maintenance)}
                    />
                  </div>
                  <div className={styles.CheckboxItem}>
                    <label>Tags</label>
                    <input
                      className={styles.Checkbox}
                      type="checkbox"
                      name='Tags'
                      checked={tags}
                      onChange={() => setTags(!tags)}
                      onClick={changePermission(user, 'see-tags', !tags)}
                    />
                  </div>
                  <div className={styles.CheckboxItem}>
                    <label>Tapped In</label>
                    <input
                      className={styles.Checkbox}
                      type="checkbox"
                      name='Tapped In'
                      checked={tappedIn}
                      onChange={() => setTappedIn(!tappedIn)}
                      onClick={changePermission(user, 'see-tapped-people', !tappedIn)}
                    />
                  </div>
                  <div className={styles.CheckboxItem}>
                    <label>Filter by people</label>
                    <input
                      className={styles.Checkbox}
                      type="checkbox"
                      name='Filter by people'
                      checked={filterReportsByPeople}
                      onChange={() => setFilterReportsByPeople(!filterReportsByPeople)}
                      onClick={changePermission(user, UserCan.FILTER_REPORTS_BY_PEOPLE, !filterReportsByPeople)}
                    />
                  </div>
                </div>
              </div>
              {/* Pages setup */}
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>Pages Setup</span>
                <div className={styles.SectionBodyTable}>
                  {pagesSetup.map(({permission, name, value, onChange}) => (
                    <div key={permission} className={styles.CheckboxItem}>
                      <label>{name}</label>
                      <input
                        className={styles.Checkbox}
                        type='checkbox'
                        name={name}
                        checked={value}
                        onChange={() => onChange(!value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>Device Info</span>
                <div className={styles.SectionBodyTable}>
                  {deviceInfo.map(({permission, name}) => {
                    const value = deviceInfoConfiguration[permission];
                    return (
                      <div key={permission} className={styles.CheckboxItem}>
                        <label>{name}</label>
                        <input
                          className={styles.Checkbox}
                          type='checkbox'
                          name={name}
                          // value means 'hide' section (eq to 'not show'), so we need to set `!value` as checked param
                          checked={!value}
                          onChange={() => deviceInfoOptionChange(permission, !value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Device info section order */}
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>Device Info Section Order</span>
                <DragSortableList
                    type="vertical"
                    placeholder={<div />}
                    onSort={onDeviceSectionReorder}
                    items={deviceInfoSectionOrderItems.map((orderItem, index) => ({
                      id: orderItem.id,
                      content: (
                          <div
                              className={`${styles.DeviceOrderItem} ${
                                  index === 0 ? styles.DeviceOrderFirstItem : ''
                              }`}
                              key={orderItem.id}
                          >
                            <div className={styles.IconAndOrderItem}>
                              <DragIcon style={{ width: '16px', height: '16px' }} />
                              <span>{orderItem.name}</span>
                            </div>
                            <div>
                              <button onClick={() => toggleHidden(orderItem.name)}>
                                <img
                                    src={
                                      hiddenMap[orderItem.name] === false
                                          ? '/assets/fa-svgs/solid/eye-slash.svg'
                                          : '/assets/fa-svgs/solid/eye.svg'
                                    }
                                    alt="toggle visibility"
                                />
                              </button>
                            </div>
                          </div>
                      ),
                    }))}
                />
              </div>
              {/* Map setup */}
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>Map setup</span>
                <div className={styles.SectionBodyTable}>
                  {mapSetup.map(({ permission, name, value, onChange }) => (
                    <div key={permission} className={styles.CheckboxItem}>
                      <label>{name}</label>
                      <input
                        className={styles.Checkbox}
                        type='checkbox'
                        name={name}
                        checked={value}
                        onChange={() => onChange(!value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>Visibility Settings</span>
                <div className={styles.SectionBodyTable}>
                  {visibilitySetup.map(({ label, name, value, onChange }) => (
                    <div key={label} className={styles.CheckboxItem}>
                      <label>{name}</label>
                      <input
                        className={styles.Checkbox}
                        type='checkbox'
                        name={name}
                        checked={label === 'pin_visibility' ? value : !value}
                        onChange={() => onChange(!value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.AllUserFeaturesSection}>
                <span className={styles.SectionTitle}>Travel Report Field Visibility</span>
                <div className={styles.SectionBodyTable}>
                  {travelReportFieldVisibility.map(({ label, name, value, onChange }) => (
                    <div key={label} className={styles.CheckboxItem}>
                      <label>{name}</label>
                      <input
                        className={styles.Checkbox}
                        type='checkbox'
                        name={name}
                        checked={!value}
                        onChange={() => onChange(!value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
          {fetchedFilterVisibility && <div className={styles.AllUserFeaturesSection}>
            <span className={styles.SectionTitle}>Report Filter Sections Visibility</span>
            <div className={styles.SectionBodyTable}>
              {reportFilterVisibilitySettings.map(({label, name, value, onChange}) => (
                <div key={label} className={styles.CheckboxItem}>
                  <label>{name}</label>
                  <input
                    className={styles.Checkbox}
                    type='checkbox'
                    name={name}
                    checked={value}
                    onChange={() => onChange(!value)}
                  />
                </div>
              ))}
            </div>
          </div>}
          {fetchedTagEditVisibility && <div className={styles.AllUserFeaturesSection}>
            <span className={styles.SectionTitle}>Tag Edit Field Visibility</span>
            <div className={styles.SectionBodyTable}>
              {tagEditFieldVisibilitySettings.map(({label, name, value, onChange}) => (
                <div key={label} className={styles.CheckboxItem}>
                  <label>{name}</label>
                  <input
                    className={styles.Checkbox}
                    type='checkbox'
                    name={name}
                    checked={value}
                    onChange={() => onChange(!value)}
                  />
                </div>
              ))}
            </div>
          </div>}
          <div className={styles.AllUserFeaturesSection}>
            <span className={styles.SectionTitle}>Other</span>
            <div className={styles.SectionBodyTable}>
              {otherSetup.map(({label, name, value, onChange}) => (
                <div key={label} className={styles.CheckboxItem}>
                      <label>{name}</label>
                      <input
                        className={styles.Checkbox}
                        type='checkbox'
                        name={name}
                        checked={value}
                        onChange={() => onChange(!value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
        </div>
      </LocalPersonTile>
    </div>
  );
};
